import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    FaShareAlt,
    FaBookmark,
    FaRegBookmark,
    FaEye,
    FaPlay,
    FaSun,
    FaMoon,
    FaFacebookF,
    FaWhatsapp,
    FaCopy,
    FaCheck,
    FaTimes,
    FaTimesCircle,
    FaSearch,
    FaFire,
    FaBars,
    FaHome,
    FaNewspaper,
    FaVideo,
    FaCamera,
    FaFootballBall,
    FaFilm,
    FaMusic,
    FaStar
} from "react-icons/fa";
import axios from "axios";
import "./Home.css";

const Home = () => {
    const navigate = useNavigate()
    const [posts, setPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [isScrolled, setIsScrolled] = useState(false);
    const [viewedArticles, setViewedArticles] = useState([]);
    const [bookmarkedArticles, setBookmarkedArticles] = useState([]);
    const [showShareModal, setShowShareModal] = useState(false);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [copied, setCopied] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [showAppPopup, setShowAppPopup] = useState(false);
    const [popupTimeout, setPopupTimeout] = useState(null);
    const [activeCategory, setActiveCategory] = useState("all");
    const [categories, setCategories] = useState([]);

    const postsPerPage = 15;

    const [darkMode, setDarkMode] = useState(() => {
        const savedTheme = localStorage.getItem('theme');
        return savedTheme === 'dark' ||
            (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
    });

    // Apply dark/light mode
    useEffect(() => {
        if (darkMode) {
            document.documentElement.setAttribute('data-theme', 'dark');
            localStorage.setItem('theme', 'dark');
        } else {
            document.documentElement.setAttribute('data-theme', 'light');
            localStorage.setItem('theme', 'light');
        }
    }, [darkMode]);

    // Function to get Tamil category name
    const getTamilCategoryName = (englishName) => {
        const mappings = {
            "Did you Know?": "உங்களுக்கு தெரியுமா?",
            "News": "செய்திகள்",
            "Sports": "விளையாட்டு",
            "Cinema": "திரைப்படம்",
            "Photo": "புகைப்படம்",
            "Video": "வீடியோ",
            "Music": "இசை",
            "Special": "சிறப்பு",
            "General": "பொது",
            "Technology": "தொழில்நுட்பம்",
            "Health": "ஆரோக்கியம்",
            "Politics": "அரசியல்"
        };
        return mappings[englishName] || englishName;
    };

    // Function to get appropriate icon for category
    const getCategoryIcon = (categoryName) => {
        const category = categoryName.toLowerCase();

        if (category.includes('செய்தி') || category.includes('news')) return <FaNewspaper />;
        if (category.includes('விளையாட்டு') || category.includes('sports')) return <FaFootballBall />;
        if (category.includes('திரைப்பட') || category.includes('cinema') || category.includes('movie')) return <FaFilm />;
        if (category.includes('புகைப்பட') || category.includes('photo')) return <FaCamera />;
        if (category.includes('வீடியோ') || category.includes('video')) return <FaVideo />;
        if (category.includes('இசை') || category.includes('music')) return <FaMusic />;
        if (category.includes('சிறப்பு') || category.includes('special')) return <FaStar />;

        return <FaNewspaper />;
    };

    // Extract categories from posts
    useEffect(() => {
        if (posts.length > 0) {
            // Get unique categories from posts
            const uniqueCategories = [];
            const categoryMap = new Map();

            posts.forEach(post => {
                if (post.category?.name) {
                    const englishCategoryName = post.category.name;
                    const tamilCategoryName = getTamilCategoryName(englishCategoryName);
                    
                    if (!categoryMap.has(englishCategoryName)) {
                        categoryMap.set(englishCategoryName, true);
                        uniqueCategories.push({
                            id: englishCategoryName.toLowerCase().replace(/\s+/g, '-'),
                            englishName: englishCategoryName,
                            name: tamilCategoryName,
                            icon: getCategoryIcon(englishCategoryName)
                        });
                    }
                }
            });

            // Sort categories alphabetically by Tamil name
            uniqueCategories.sort((a, b) => a.name.localeCompare(b.name));

            // Add "All" category at the beginning
            const allCategories = [
                { 
                    id: "all", 
                    englishName: "all",
                    name: "அனைத்தும்", 
                    icon: <FaHome /> 
                }
            ].concat(uniqueCategories);

            setCategories(allCategories);
        }
    }, [posts]);

    // Show Open in App popup after 2 seconds
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowAppPopup(true);
        }, 2000);

        return () => clearTimeout(timer);
    }, []);

    // Handle dismiss popup
    const dismissPopup = () => {
        setShowAppPopup(false);
        if (popupTimeout) {
            clearTimeout(popupTimeout);
        }
        const newTimeout = setTimeout(() => {
            setShowAppPopup(true);
        }, 20000);
        setPopupTimeout(newTimeout);
    };

    // Handle open app
    const handleOpenApp = () => {
        const appDeepLink = "lookit://home";
        const playStoreLink = "https://play.google.com/store/apps/details?id=com.makkal.calendar&pcampaignid=web_share";

        window.location.href = appDeepLink;
        setTimeout(() => {
            if (document.hidden) {
                setShowAppPopup(false);
            } else {
                window.location.href = playStoreLink;
            }
        }, 500);
        setShowAppPopup(false);
    };

    // Handle scroll for header
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Load viewed and bookmarked articles
    useEffect(() => {
        const viewed = JSON.parse(localStorage.getItem('viewedArticles') || '[]');
        const bookmarked = JSON.parse(localStorage.getItem('bookmarkedArticles') || '[]');
        setViewedArticles(viewed);
        setBookmarkedArticles(bookmarked);
    }, []);

    // Fetch posts
    const fetchPosts = useCallback(() => {
        setIsLoading(true);
        axios
            .get(`https://tnreaders.in/mobile/list-posts?perPage=1000&currentPage=1`)
            .then((response) => {
                const activePosts = (response.data.data || []).filter(
                    (post) => post.isActive === "yes"
                );

                console.log("Fetched posts:", activePosts);

                setPosts(activePosts);
                setTotalPages(Math.ceil(activePosts.length / postsPerPage));
                setIsLoading(false);
            })
            .catch((error) => {
                console.error("API fetch error:", error);
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    // Clean up timeout
    useEffect(() => {
        return () => {
            if (popupTimeout) {
                clearTimeout(popupTimeout);
            }
        };
    }, [popupTimeout]);

    // Pagination logic with category filtering
    const paginatedPosts = React.useMemo(() => {
        const startIndex = (currentPage - 1) * postsPerPage;
        const endIndex = startIndex + postsPerPage;

        let filtered = [...posts];

        // Filter by selected category
        if (activeCategory !== "all") {
            const selectedCat = categories.find(cat => cat.id === activeCategory);
            if (selectedCat) {
                filtered = filtered.filter(post =>
                    post.category?.name?.toLowerCase() === selectedCat.englishName.toLowerCase()
                );
            }
        }

        // Filter by search query
        if (searchQuery.trim()) {
            filtered = filtered.filter(
                (post) =>
                    post.title && post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    (post.description && post.description.toLowerCase().includes(searchQuery.toLowerCase()))
            );
        }

        setTotalPages(Math.ceil(filtered.length / postsPerPage));

        return filtered.slice(startIndex, endIndex);
    }, [posts, currentPage, searchQuery, activeCategory, categories]);

    // Track article view
    const trackArticleView = (articleId) => {
        const newViewed = [...new Set([articleId, ...viewedArticles])].slice(0, 20);
        setViewedArticles(newViewed);
        localStorage.setItem('viewedArticles', JSON.stringify(newViewed));
    };

    // Toggle bookmark
    const toggleBookmark = (articleId, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        let newBookmarks;
        if (bookmarkedArticles.includes(articleId)) {
            newBookmarks = bookmarkedArticles.filter(id => id !== articleId);
        } else {
            newBookmarks = [...bookmarkedArticles, articleId];
        }
        setBookmarkedArticles(newBookmarks);
        localStorage.setItem('bookmarkedArticles', JSON.stringify(newBookmarks));
    };

    // Share functionality
    const openShareModal = (article, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setSelectedArticle(article);
        setShowShareModal(true);
    };

    const copyToClipboard = (articleId, articleSlug) => {
        const urlSlug = articleSlug || articleId;
        const url = `${window.location.origin}/article-detail/${urlSlug}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const shareOnFacebook = (articleId, articleSlug) => {
        const urlSlug = articleSlug || articleId;
        const url = encodeURIComponent(`${window.location.origin}/article-detail/${urlSlug}`);
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
        setShowShareModal(false);
    };

    const shareOnWhatsApp = (articleId, articleSlug) => {
        const urlSlug = articleSlug || articleId;
        const text = encodeURIComponent(`Check out this article: ${window.location.origin}/article-detail/${urlSlug}`);
        window.open(`https://wa.me/?text=${text}`, '_blank');
        setShowShareModal(false);
    };

    const toggleDarkMode = () => {
        setDarkMode(prevMode => !prevMode);
    };

    // Skeleton Loader
    const SkeletonLoader = () => (
        <div className="skeleton-loader">
            <div className="skeleton-header"></div>
            <div className="skeleton-nav"></div>
            <div className="skeleton-content">
                {[...Array(8)].map((_, i) => (
                    <div key={i} className="skeleton-article"></div>
                ))}
            </div>
        </div>
    );

    if (isLoading) {
        return <SkeletonLoader />;
    }

    return (
        <div className="lookit-container">
            {/* Top Header */}
            <header className="top-header">
                <div className="header-left">
                    <button className="menu-toggle" onClick={() => setShowMobileMenu(!showMobileMenu)}>
                        <FaBars />
                    </button>
                    <div className="date-time">
                        <span className="date">{new Date().toLocaleDateString('ta-IN')}</span>
                        <span className="time">{new Date().toLocaleTimeString('ta-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                </div>

                <div className="logo-container">
                    <img
                        src="/assets/unnamed1.png"
                        alt="Lookit"
                        className="main-logo"
                        onClick={() => {navigate("/")}}
                        style={{cursor:"pointer"}}
                        onError={(e) => {
                            e.target.src = "/assets/unnamed1.png";
                        }}
                    />
                </div>

                <div className="header-right">
                    <button className="theme-toggle" onClick={toggleDarkMode}>
                        {darkMode ? <FaSun /> : <FaMoon />}
                    </button>
                </div>
            </header>

            {/* Mobile Menu with Dynamic Categories */}
            {showMobileMenu && (
                <div className="mobile-menu-overlay" onClick={() => setShowMobileMenu(false)}>
                    <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
                        <div className="menu-header">
                            <h3>பிரிவுகள்</h3>
                            <button className="close-menu" onClick={() => setShowMobileMenu(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="menu-categories">
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    className={`menu-category ${activeCategory === cat.id ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveCategory(cat.id);
                                        setCurrentPage(1);
                                        setShowMobileMenu(false);
                                    }}
                                >
                                    <span className="menu-icon">{cat.icon}</span>
                                    <span className="menu-name">{cat.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Categories Navigation with Dynamic Categories */}
            <nav className="categories-nav">
                <div className="categories-container">
                    {categories.map(cat => (
                        <button
                            key={cat.id}
                            className={`category-btn ${activeCategory === cat.id ? 'active' : ''}`}
                            onClick={() => {
                                setActiveCategory(cat.id);
                                setCurrentPage(1);
                            }}
                        >
                            <span className="cat-icon">{cat.icon}</span>
                            <span className="cat-name">{cat.name}</span>
                        </button>
                    ))}
                </div>
            </nav>

            {/* Category Info */}
            {activeCategory !== "all" && (
                <div className="category-info">
                    <h3>
                        {categories.find(cat => cat.id === activeCategory)?.name}
                        <span className="article-count">
                            ({paginatedPosts.length} கட்டுரைகள்)
                        </span>
                    </h3>
                </div>
            )}

            {/* Breaking News Bar */}
            <div className="breaking-news">
                <div className="breaking-label">
                    <FaFire /> BREAKING NEWS
                </div>
                <div className="breaking-content">
                    <marquee>
                        {posts.filter(p => p.istrending === 1).map((post, index) => (
                            <span key={post.id} className="breaking-item">
                                {post.title} {index < posts.filter(p => p.istrending === 1).length - 1 && ' • '}
                            </span>
                        ))}
                    </marquee>
                </div>
            </div>

            {/* Main Content */}
            <main className="main-content">
                {/* Search Bar */}
                <div className="search-section">
                    <div className="search-box">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="செய்திகளை தேடு..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                        {searchQuery && (
                            <button className="clear-search" onClick={() => setSearchQuery('')}>
                                <FaTimes />
                            </button>
                        )}
                    </div>
                </div>

                {/* Articles Grid - lookit Style */}
                <div className="articles-grid">
                    {paginatedPosts.length > 0 ? (
                        paginatedPosts.map((post, index) => (
                            <article key={post.id} className={`article-card ${index < 3 ? 'featured' : ''} ${viewedArticles.includes(post.id) ? 'viewed' : ''}`}>
                                <Link
                                    to={`/article-detail/${post.seo_slug || post.id}`}
                                    className="article-link"
                                    onClick={() => trackArticleView(post.id)}
                                >
                                    {/* Article Image */}
                                    <div className="article-image">
                                        <img
                                            src={post.web_thumbnail || '/images/placeholder.jpg'}
                                            alt={post.title}
                                            loading="lazy"
                                            onError={(e) => {
                                                e.target.src = "/assets/unnamed.png";
                                            }}
                                        />

                                        {/* Category Badge */}
                                        {post.category?.name && (
                                            <span className="category-badge">
                                                {getTamilCategoryName(post.category.name)}
                                            </span>
                                        )}

                                        {/* Trending Badge */}
                                        {post.istrending === 1 && (
                                            <span className="trending-badge">
                                                <FaFire /> பிரபலமான
                                            </span>
                                        )}

                                        {/* Video Icon */}
                                        {(post.content_type === "video" || post.content_type === "shorts") && (
                                            <div className="video-icon">
                                                <FaPlay />
                                            </div>
                                        )}
                                    </div>

                                    {/* Article Content */}
                                    <div className="article-content">
                                        <h3 className="article-title">{post.title}</h3>

                                        <div className="article-meta">
                                            <time className="article-date">
                                                {new Date(post.created_at).toLocaleDateString('ta-IN', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                })}
                                            </time>
                                            <span className="article-time">
                                                {new Date(post.created_at).toLocaleTimeString('ta-IN', {
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </span>
                                        </div>

                                        {post.description && index < 3 && (
                                            <p className="article-excerpt">
                                                {post.description.substring(0, 150)}...
                                            </p>
                                        )}

                                        {/* Action Buttons */}
                                        <div className="article-actions">
                                            <button
                                                className={`bookmark-btn ${bookmarkedArticles.includes(post.id) ? 'bookmarked' : ''}`}
                                                onClick={(e) => toggleBookmark(post.id, e)}
                                            >
                                                {bookmarkedArticles.includes(post.id) ? <FaBookmark /> : <FaRegBookmark />}
                                            </button>
                                            <button
                                                className="share-btn"
                                                onClick={(e) => openShareModal(post, e)}
                                            >
                                                <FaShareAlt />
                                            </button>
                                            {viewedArticles.includes(post.id) && (
                                                <span className="viewed-badge">
                                                    <FaEye /> பார்த்தது
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            </article>
                        ))
                    ) : (
                        <div className="no-articles">
                            <div className="no-articles-icon">📰</div>
                            <h3>கட்டுரைகள் இல்லை</h3>
                            <p>தேர்ந்தெடுக்கப்பட்ட பிரிவில் கட்டுரைகள் இல்லை</p>
                            <button
                                className="reset-category"
                                onClick={() => setActiveCategory("all")}
                            >
                                அனைத்து கட்டுரைகளையும் காட்டு
                            </button>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && paginatedPosts.length > 0 && (
                    <div className="pagination">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="pagination-btn prev"
                        >
                            முந்தைய
                        </button>

                        <div className="page-numbers">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                } else {
                                    pageNum = currentPage - 2 + i;
                                }

                                return (
                                    <button
                                        key={pageNum}
                                        className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                                        onClick={() => setCurrentPage(pageNum)}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="pagination-btn next"
                        >
                            அடுத்தது
                        </button>
                    </div>
                )}
            </main>

            {/* Open in App Popup */}
            {showAppPopup && (
                <div className="app-popup-overlay">
                    <div className="app-popup">
                        <button className="popup-close" onClick={dismissPopup}>
                            <FaTimesCircle />
                        </button>
                        <div className="popup-content">
                            <div className="popup-icon">
                                <img src="/assets/unnamed.png" alt="Lookit App" />
                            </div>
                            <h3>Open in App</h3>
                            <p>For Beeter Experience use this Mobile App</p>
                            <p>சிறந்த அனுபவத்திற்கு எங்கள் மொபைல் ஆப்ஸைப் பயன்படுத்தவும்</p>
                            <div className="popup-buttons">
                                <button className="popup-open-app" onClick={handleOpenApp}>
                                    Download App
                                </button>
                                <button className="popup-dismiss" onClick={dismissPopup}>
                                    Continue in Web
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Share Modal */}
            {showShareModal && selectedArticle && (
                <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
                    <div className="modal-content share-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>பகிர்</h3>
                            <button className="modal-close" onClick={() => setShowShareModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="share-options">
                                <button className="share-option facebook" onClick={() => shareOnFacebook(selectedArticle.id, selectedArticle.seo_slug)}>
                                    <FaFacebookF />
                                    <span>Facebook</span>
                                </button>
                                <button className="share-option whatsapp" onClick={() => shareOnWhatsApp(selectedArticle.id, selectedArticle.seo_slug)}>
                                    <FaWhatsapp />
                                    <span>WhatsApp</span>
                                </button>
                                <button className="share-option copy-link" onClick={() => copyToClipboard(selectedArticle.id, selectedArticle.seo_slug)}>
                                    {copied ? <FaCheck /> : <FaCopy />}
                                    <span>{copied ? 'காப்பி செய்யப்பட்டது!' : 'இணைப்பை காப்பி செய்யவும்'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Home;